import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, Switch,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import api from '../api';

export default function VatRatesModal({ isOpen, onClose }) {
  const [rates, setRates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', rate: '', description: '' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchRates = useCallback(async () => {
    try { const { data } = await api.get('/vat-rates'); setRates(data); } catch(e) {}
  }, []);

  useEffect(() => { if (isOpen) { fetchRates(); setShowForm(false); } }, [isOpen, fetchRates]);

  const handleSave = async () => {
    if (!form.code || form.rate === '') { toast({ title: 'Code and rate required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      await api.post('/vat-rates', form);
      await fetchRates(); setShowForm(false); setForm({ code: '', rate: '', description: '' });
      toast({ title: 'VAT rate added', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const toggleAvailable = async (id, current) => {
    try {
      await api.put(`/vat-rates/${id}`, { is_available: !current });
      await fetchRates();
    } catch(e) {}
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay /><ModalContent>
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            VAT Rates
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Add'}</Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>VAT Code *</FormLabel>
                    <Input size="xs" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} placeholder="e.g. S" /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Rate % *</FormLabel>
                    <Input size="xs" type="number" step="0.01" value={form.rate} onChange={e => setForm(p => ({ ...p, rate: e.target.value }))} /></FormControl>
                </HStack>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                  <Input size="xs" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Standard Rate" /></FormControl>
                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">Add Rate</Button>
              </VStack>
            </Box>
          )}
          {rates.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>No VAT rates configured.</Text>
          ) : (
            <VStack spacing={1} align="stretch">
              {rates.map(r => (
                <Flex key={r.id} p={2} border="1px" borderColor={r.is_available ? 'gray.100' : 'red.100'}
                  borderRadius="md" align="center" justify="space-between"
                  opacity={r.is_available ? 1 : 0.5} bg={r.is_available ? 'white' : 'red.50'}>
                  <HStack spacing={3}>
                    <Badge fontSize="10px" colorScheme="blue" variant="solid" minW="30px" textAlign="center">{r.code}</Badge>
                    <Text fontSize="13px" fontWeight="600">{r.rate}%</Text>
                    {r.description && <Text fontSize="11px" color="gray.500">{r.description}</Text>}
                    {!r.is_available && <Badge fontSize="8px" colorScheme="red">Unavailable</Badge>}
                  </HStack>
                  <Switch size="sm" isChecked={r.is_available} colorScheme="green"
                    onChange={() => toggleAvailable(r.id, r.is_available)} />
                </Flex>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
